import { produce } from 'immer'
import { transactionDetailRouteProps } from 'src/navigation/routeProps'
import {
  UPLOAD_ATTACHMENT_FAILURE,
  UPLOAD_ATTACHMENT_INIT,
  UPLOAD_ATTACHMENT_SUCCESS,
  UploadAttachmentActions,
} from 'src/redux/actions/data/receiptAttachments.actions'
import { createFlyoutReducer } from 'src/redux/genericReducers'

export interface TransactionDetailState {
  uploadingAttachmentThumbnails: Record<string, string>
  uploadingAttachmentFileNames: Record<string, string>
  failedUploadThumbnails: Record<string, string>
  failedUploadFileNames: Record<string, string>
  recentlyUploadedAttachmentIds: Set<string>
}

export const initialState: TransactionDetailState = {
  uploadingAttachmentThumbnails: {},
  uploadingAttachmentFileNames: {},
  failedUploadThumbnails: {},
  failedUploadFileNames: {},
  recentlyUploadedAttachmentIds: new Set(),
}

export type Actions = UploadAttachmentActions

const CoreTransactionDetailReducer = (
  state = initialState,
  action: Actions,
): TransactionDetailState => {
  return produce(state, (draft) => {
    switch (action.type) {
      case UPLOAD_ATTACHMENT_INIT: {
        draft.uploadingAttachmentThumbnails = {
          ...draft.uploadingAttachmentThumbnails,
          [action.payload.uploadId]: action.payload.thumbnailUrl,
        }
        draft.uploadingAttachmentFileNames = {
          ...draft.uploadingAttachmentFileNames,
          [action.payload.uploadId]: action.payload.fileName,
        }
        return draft
      }

      case UPLOAD_ATTACHMENT_FAILURE: {
        const failedId = action.payload.uploadId
        const thumbnailUrl = draft.uploadingAttachmentThumbnails[failedId]
        if (thumbnailUrl) {
          draft.failedUploadThumbnails[failedId] = thumbnailUrl
        }
        const fileName = draft.uploadingAttachmentFileNames[failedId]
        if (fileName) {
          draft.failedUploadFileNames[failedId] = fileName
        }
        draft.uploadingAttachmentThumbnails = Object.keys(
          draft.uploadingAttachmentThumbnails,
        )
          .filter((id) => id !== failedId)
          .reduce(
            (acc, curr) => ({
              ...acc,
              [curr]: draft.uploadingAttachmentThumbnails[curr],
            }),
            {},
          )
        draft.uploadingAttachmentFileNames = Object.keys(
          draft.uploadingAttachmentFileNames,
        )
          .filter((id) => id !== failedId)
          .reduce(
            (acc, curr) => ({
              ...acc,
              [curr]: draft.uploadingAttachmentFileNames[curr],
            }),
            {},
          )
        return draft
      }

      case UPLOAD_ATTACHMENT_SUCCESS: {
        const successId = action.payload.uploadId
        draft.uploadingAttachmentThumbnails = Object.keys(
          draft.uploadingAttachmentThumbnails,
        )
          .filter((id) => id !== successId)
          .reduce(
            (acc, curr) => ({
              ...acc,
              [curr]: draft.uploadingAttachmentThumbnails[curr],
            }),
            {},
          )
        draft.uploadingAttachmentFileNames = Object.keys(
          draft.uploadingAttachmentFileNames,
        )
          .filter((id) => id !== successId)
          .reduce(
            (acc, curr) => ({
              ...acc,
              [curr]: draft.uploadingAttachmentFileNames[curr],
            }),
            {},
          )
        draft.recentlyUploadedAttachmentIds = new Set(
          draft.recentlyUploadedAttachmentIds,
        ).add(action.payload.id)
        return draft
      }
    }
  })
}

const TransactionDetailReducer = createFlyoutReducer(
  transactionDetailRouteProps,
  initialState,
  CoreTransactionDetailReducer,
)

export default TransactionDetailReducer
